import { StrictMode, useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router'
import { Toaster } from 'sonner'
import './index.css'
import { ErrorBoundary } from "@/components/ErrorBoundary"
import { CookieConsentBanner } from "@/components/CookieConsentBanner"
import { hasAnalyticsConsent } from "@/lib/cookie-consent"
import { registerServiceWorker } from "@/lib/pwa"
import App from './App.tsx'
import AppointmentConfirmation from './pages/AppointmentConfirmation'

const isConfirmation = window.location.pathname === "/appointment-confirmation";
const confirmationToken = isConfirmation ? window.location.hash.slice(1) : "";
if (isConfirmation) window.history.replaceState(null, "", window.location.pathname);

function ConfirmationPage() {
  const [token, setToken] = useState(confirmationToken);
  useEffect(() => {
    const onHashChange = () => {
      const nextToken = window.location.hash.slice(1);
      if (!nextToken) return;
      window.history.replaceState(null, "", window.location.pathname);
      setToken(nextToken);
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);
  return <AppointmentConfirmation key={token} token={token} />;
}

// Sentry is heavy — dynamic-import only after analytics consent so marketing
// Home never pays for @sentry/react on the critical path.
if (!isConfirmation && hasAnalyticsConsent()) {
  void import("@/lib/sentry").then((m) => m.initSentry());
}

// Defer SW so marketing LCP/TBT aren't competing with registration work.
const deferSw = () => {
  void registerServiceWorker();
};
const ric = (
  window as Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  }
).requestIdleCallback;
if (!isConfirmation && typeof ric === "function") {
  ric(deferSw, { timeout: 4000 });
} else if (!isConfirmation) {
  window.addEventListener("load", () => setTimeout(deferSw, 1), { once: true });
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {isConfirmation ? <ConfirmationPage /> : <BrowserRouter>
      <ErrorBoundary>
        <App />
        <CookieConsentBanner />
      </ErrorBoundary>
      <Toaster position="top-right" richColors />
    </BrowserRouter>}
  </StrictMode>,
)
